import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, forwardRef, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { extractErrors } from '@vsolv/dev-kit/ngx';
import { Property, PropertySet } from '@vsolv/packages/properties/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { BehaviorSubject, catchError, firstValueFrom, map, of, takeUntil } from 'rxjs';
import { createPropertyControl } from '../properties';
import { StaticPropertyFormComponent } from '../static-property-form.component';

export interface VehicleResponse {
  Results: [
    {
      Make: string;
      MakeID: string;
      Model: string;
      ModelID: string;
      Manufacturer: string;
      ManufacturerId: string;
      ModelYear: string;
      VehicleType: string;
      FuelTypePrimary: string;
      FuelInjectionType: string;
      EngineConfiguration: string;
      EngineCylinders: string;
      EngineManufacturer: string;
      EngineModel: string;
      BodyClass: string;
      Doors: string;
      Windows: string;
      OtherEngineInfo: string;
      Trim: string;
      Trim2: string;
      Series: string;
      Series2: string;
    },
  ];
}

type VehicleAssignment = Property.ObjectPropertyAssignment & {
  property: Property.Model<Property.PropertyType>;
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'vs-vehicle-property-input',
  templateUrl: './vehicle-property-input.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => VehiclePropertyInputComponent),
    },
    { provide: NG_VALIDATORS, useExisting: VehiclePropertyInputComponent, multi: true },
  ],
})
export class VehiclePropertyInputComponent extends StaticPropertyFormComponent implements OnInit {
  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private toastSvc: ToastService
  ) {
    super();
  }
  vehicleCheckbox = new FormControl(false);

  overlayOpen = false;
  expanded = false;
  detailsExpanded = false;

  autofillSearch$ = new BehaviorSubject<string>('');

  override property!: Property.ObjectModel;

  assignments: VehicleAssignment[] = [];
  visibleExpandedAssignments: VehicleAssignment[] = [];
  fieldRoles: Partial<Record<PropertySet.VehicleFieldRole, VehicleAssignment>> = {};

  form = this.fb.group({}) as FormGroup<Record<string, FormControl>>;

  vehicle$ = new BehaviorSubject<VehicleResponse['Results'][number] | null>(null);

  formValid$ = this.form.statusChanges.pipe(map(status => status === 'VALID'));

  async useVIN(vin: string) {
    // use api to get vehicle details
    const vehicle = await firstValueFrom(
      this.http.get<VehicleResponse>(`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/${vin}?format=json`).pipe(
        catchError(err => {
          console.error(err);
          return of(null);
        })
      )
    );

    if (!vehicle) {
      this.toastSvc.show({
        title: 'Vehicle not found',
        text: 'Could not find vehicle. Please check your VIN and try again.',
        type: 'warning',
      });
      return;
    }

    const results = vehicle.Results[0];
    this.vehicle$.next(results);

    if (!results) {
      this.toastSvc.show({
        title: 'Vehicle not found',
        text: 'Could not find vehicle with vin + ' + vin,
        type: 'warning',
      });
    }
    if (!results.Make) {
      this.toastSvc.show({
        title: 'Could not find Vehicle Make',
        text: 'Could not find vehicle make with vin ' + vin,
        type: 'warning',
      });
    }
    if (!results.Model) {
      this.toastSvc.show({
        title: 'Could not find Vehicle Model',
        text: 'Could not find vehicle model with vin ' + vin,
        type: 'warning',
      });
    }
    if (!results.ModelYear) {
      this.toastSvc.show({
        title: 'Could not find Vehicle Year',
        text: 'Could not find vehicle year with vin ' + vin,
        type: 'warning',
      });
    }

    this.form.patchValue(this.buildVinPatch(results, vin));
  }

  open() {
    this.overlayOpen = true;
  }

  close() {
    this.overlayOpen = false;
  }

  toggle() {
    this.expanded = !this.expanded;
  }

  get vinAssignment(): VehicleAssignment | undefined {
    return this.fieldRoles.vin;
  }

  get vinControl(): FormControl | null {
    const assignment = this.vinAssignment;
    if (!assignment) return null;

    return this.form.get(assignment.property.valueKey) as FormControl | null;
  }

  getSummaryValue(role: Exclude<PropertySet.VehicleFieldRole, 'vin'>): string | number | null {
    const assignment = this.fieldRoles[role];
    if (!assignment) return null;

    return this.form.get(assignment.property.valueKey)?.value ?? null;
  }

  override writeValue(value: unknown): void {
    super.writeValue(value);

    if (value && typeof value === 'object') {
      this.form.patchValue(value as Partial<typeof this.form.value>, { emitEvent: false });
      this.syncValidityAfterValueLoad();
    }
  }

  /**
   * Nested required children (e.g. a newly added Manufacturer field) live on `this.form`.
   * The outer `formControl` only has Validators.required on the object itself, so without
   * this override an incomplete existing vehicle still looks valid.
   */
  override validate(): ValidationErrors | null {
    if (this.form) {
      const formErrors = extractErrors(this.form);
      if (formErrors) {
        this.valid = false;
        return formErrors;
      }
    }
    return super.validate();
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.assignments = this.getAssignments();
    this.fieldRoles = this.resolveFieldRoles(this.assignments);
    this.visibleExpandedAssignments = this.assignments.filter(assignment => !this.isVinAssignment(assignment));
    this.form = this.buildForm(this.assignments);
    this.formValid$ = this.form.statusChanges.pipe(map(status => status === 'VALID'));

    const existingValue = this.formControl?.value;
    if (existingValue && typeof existingValue === 'object') {
      this.form.patchValue(existingValue as Record<string, unknown>, { emitEvent: false });
    }

    this.syncValidityAfterValueLoad();

    const customerVehicle =
      this.extraData && this.matchesAssignedField(this.extraData['vehicle'], this.fieldRoles.make)
        ? this.extraData['vehicle']
        : undefined;
    if (customerVehicle) {
      this.formControl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(value => {
        this.vehicleCheckbox.setValue(JSON.stringify(customerVehicle) === JSON.stringify(value));
      });
    }

    this.form.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      // Keep partial values so the user can finish filling newly required fields.
      this.formControl.setValue(this.form.value);
    });
  }

  /** Expand + surface required errors when loaded values miss newly required children. */
  private syncValidityAfterValueLoad() {
    if (!this.form || this.form.valid) return;

    this.expanded = true;
    this.form.markAllAsTouched();
    this.formControl.updateValueAndValidity({ emitEvent: false });
  }

  private getAssignments(): VehicleAssignment[] {
    return (this.property.properties ?? [])
      .filter((assignment: Property.ObjectPropertyAssignment): assignment is VehicleAssignment =>
        Boolean(assignment.property)
      )
      .sort((a: VehicleAssignment, b: VehicleAssignment) => a.order - b.order);
  }

  private buildForm(assignments: VehicleAssignment[]): FormGroup<Record<string, FormControl>> {
    const controls = assignments.reduce<Record<string, FormControl>>((current, assignment) => {
      const control = createPropertyControl(assignment.property, assignment.required);

      if (this.fieldRoles.year?.property.valueKey === assignment.property.valueKey) {
        control.addValidators([Validators.pattern('^[0-9]*$'), Validators.minLength(4), Validators.maxLength(4)]);
      }

      current[assignment.property.valueKey] = control;
      return current;
    }, {});

    return new FormGroup(controls);
  }

  private resolveFieldRoles(
    assignments: VehicleAssignment[]
  ): Partial<Record<PropertySet.VehicleFieldRole, VehicleAssignment>> {
    return assignments.reduce<Partial<Record<PropertySet.VehicleFieldRole, VehicleAssignment>>>((roles, assignment) => {
      const role = this.resolveAssignmentRole(assignment);
      if (role && !roles[role]) roles[role] = assignment;

      return roles;
    }, {});
  }

  private resolveAssignmentRole(assignment: VehicleAssignment): PropertySet.VehicleFieldRole | null {
    return PropertySet.getVehicleFieldRole([
      assignment.displayLabel,
      assignment.property.name,
      assignment.property.valueKey,
    ]);
  }

  private isVinAssignment(assignment: VehicleAssignment): boolean {
    return this.fieldRoles.vin?.property.valueKey === assignment.property.valueKey;
  }

  private buildVinPatch(
    results: VehicleResponse['Results'][number],
    vin: string
  ): Record<string, string | number | null> {
    const patch: Record<string, string | number | null> = {};

    this.setPatchValue(patch, this.fieldRoles.vin, vin);
    this.setPatchValue(patch, this.fieldRoles.make, results.Make || null);
    this.setPatchValue(patch, this.fieldRoles.model, results.Model || null);
    this.setPatchValue(patch, this.fieldRoles.year, parseInt(results.ModelYear, 10) || new Date().getFullYear());

    const trimValue = [results.Trim, results.Trim2].filter(Boolean).join(' ').trim();
    if (trimValue) this.setPatchValue(patch, this.fieldRoles.trim, trimValue);

    return patch;
  }

  private setPatchValue(
    patch: Record<string, string | number | null>,
    assignment: VehicleAssignment | undefined,
    value: string | number | null
  ) {
    if (!assignment) return;

    patch[assignment.property.valueKey] = value;
  }

  private matchesAssignedField(value: unknown, assignment: VehicleAssignment | undefined): boolean {
    if (!assignment || !value || typeof value !== 'object') return false;

    return Boolean((value as Record<string, unknown>)[assignment.property.valueKey]);
  }
}
